/** @jsxImportSource @emotion/react */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { tb } from 'components/layouts/Table';
import { toggleClass } from '@grapecity/wijmo';
import cloneDeep from 'lodash/cloneDeep';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import { IconButton } from 'components/buttons/IconSVG';
import { CrudCode } from 'models/common/Edit';
import { useTranslation } from 'react-i18next';
import { Code, commonYNList } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import useEvent from 'react-use-event-hook';
import dayjs from 'dayjs';
import { InputDate } from '@grapecity/wijmo.input';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import _ from 'lodash';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useSessionStore from 'stores/useSessionStore';
import { CommonYN, FileTypeName, PageMode } from 'models/common/Common';
import { MenuContextType } from 'models/admin/Menu';
import { MenuContext } from 'App';
import { useLoading } from 'components/process/Loading';
import { CommonUtil } from 'utils/CommonUtil';
import { SetupProject } from 'models/setup/SetupProject';
import {
  checkAvailableProjectManager,
  findAssignedProject,
  findProjectById,
} from 'apis/setup/SetupProject';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { SetupEquipment, SetupEquipmentCondition } from 'models/setup/SetupEquipment';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import {
  findSampleSchedule,
  findSetupEquipment,
  findSetupEquipmentSummary,
  saveSetupEquipment,
} from 'apis/setup/SetupEquipment';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import DatepickerModal from 'components/modals/common/DatepickerModal';
import { EquipmentGroupPopUp } from 'pages/common/popup/EquipmentGroupPopUp';
import { EquipmentGroupCondition } from 'models/common/popup/EquipmentGroup';
import EquipmentPopUp from 'pages/common/popup/EquipmentPopUp';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { hasRole } from 'utils/SessionUtil';
import { findValidProjectStepType } from 'apis/setup/SetupStep';
import { SetupStep } from 'models/setup/SetupStep';

interface PageLocationState {
  mode?: PageMode;
  elmPrjId?: string;
}

interface TargetCondition {
  row: number;
  col: number;
  spTpCd: string; // [26.08.06] 단계유형에 설정된 설비군만 조회하기 위해 단계유형 선택 후 설비군 팝업 오픈
}

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 설비리스트 등록/조회 > 설비리스트 등록
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.26   jshwang12      M-CODE 규칙 변경 (최소 4자이상, 공정+설비군 포함)
 */
const SetupEquipmentRegistPage = (props) => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const menuContext = useContext<MenuContextType>(MenuContext);
  const [errors, setErrors] = useState<any>({});

  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const location = useLocation();
  const state = { ...location.state };
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  // 수정 여부
  const [elmPrjId, setElmPrjId] = useState<string>(locationState.elmPrjId ?? '');
  const [isReadOnly, setReadOnly] = useState<boolean>(false);

  const userSession = useSessionStore();

  const sampleGridRef = useRef<any>();
  const setupGridRef = useRef<any>();

  const [sampleFlexRef, setSampleFlexRef] = useState<any>();
  const [setupFlexRef, setSetupFlexRef] = useState<any>();

  const [editMode, setEditMode] = useState<string>(locationState?.mode ?? 'READ');
  const [pageRegStatCd, setPageRegStatCd] = useState<string>('');

  const [masterData, setMasterData] = useState<SetupProject>({});
  const [setupSummaryData, setSetupSummaryData] = useState<SetupProject>({});

  const [sampleRowData, setSampleRowData] = useState<SetupEquipment[]>([]);
  const [setupRowData, setSetupRowData] = useState<SetupEquipment[]>([]);

  const [bildPlntCd, setBildPlntCd] = useState<Code[]>([]);
  const [eltrTpCd, setEltrTpCd] = useState<Code[]>([]);
  const [prdnProcCode, setPrdnProcCode] = useState<Code[]>([]);
  const [prjSmplProcCd, setPrjSmplProcCd] = useState<Code[]>([]);
  const [spTpCds, setSpTpCds] = useState<SetupStep[]>([]);

  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);

  const [date, setDate] = useState<string>();
  const [isDatepickerModalOpen, setDatepickerModalOpen] = useState<boolean>(false);

  const [targetCondition, setTargetCondition] = useState<TargetCondition>();
  const [isOpenEquipmentGroupPopup, setOpenEquipmentGroupPopup] = useState<boolean>(false);

  const [equipmentModalCondition, setEquipmentModalCondition] = useState<any>({});
  const [isOpenEquipmentModal, setOpenEquipmentModal] = useState<boolean>(false);

  const [hasModifyAuth, setHasModifyAuth] = useState<boolean>(false);

  const dateEditor = useRef(
    new InputDate(document.createElement('div'), {
      format: 'yyyy.MM.dd',
      isRequired: false,
    })
  );
  const { userId, empNm } = useSessionStore();

  const isEditable = useMemo(() => {
    // 신규 / 수정모드일 경우
    if (editMode === PageMode.WRITE || editMode === PageMode.EDIT) {
      return true;
    } else {
      return false;
    }
  }, [elmPrjId, editMode]);

  // 수정권한체크 -> FA 담당자(본사/법인), FA_MANAGER
  useEffect(() => {
    (async () => {
      let auth = false;
      if (hasRole('FA_MANAGER')) {
        auth = true;
      } else {
        await checkAvailableProjectManager(elmPrjId, 'ALL').then((res) => (auth = res));
      }
      setHasModifyAuth(auth);
    })();
  }, [elmPrjId, editMode]);

  const isEditing = useMemo(() => {
    let result = false;
    if (sampleRowData && sampleRowData.length > 0) {
      result = true;
    }
    if (setupRowData && setupRowData.length > 0) {
      result = true;
    }
    return result;
  }, [sampleRowData, setupRowData]);

  // 임시저장상태일때 사용 가능 체크
  const isAvailableDraftMode = useMemo(() => {
    //임시저장 상태일때 작성자만 접근가능
    if (setupSummaryData?.regStatCd === 'SAVE' && setupSummaryData?.dataInsUserId !== userId) {
      return false;
    } else {
      return true;
    }
  }, [editMode, setupSummaryData]);

  useEffect(() => {
    //공통코드 호출
    getCommonCodes();
    // setDefaultConditionAndSearch();
  }, []);

  useEffect(() => {
    if (state?.elmPrjId) {
      setEditMode(PageMode.READ);
      setLocationState(state);
    }
  }, [location]);

  useEffect(() => {
    if (setupSummaryData?.elmPrjId) {
      let isCloseTab = false;
      // 프로젝트 정보 조회후 -> 권한체크
      if (!isAvailableDraftMode) {
        openMessageBar({
          type: 'error',
          content: t(
            'com.label.해당 프로젝트의 작성자가 아닙니다.',
            '해당 프로젝트의 작성자가 아닙니다.'
          ),
        });

        isCloseTab = true;
      }

      if (isCloseTab) {
        menuContext.closeCurrentTab(menuContext);
        return;
      }
    }
  }, [setupSummaryData]);

  const getCommonCodes = async () => {
    const bildPlntCd: Code[] = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCd(bildPlntCd); // 공장

    const eltrTpCd: Code[] = await getCommonCodeNames('FA_ELTR_TP_CD');
    setEltrTpCd(eltrTpCd);

    const prjSmplProcCd: Code[] = await getCommonCodeNames('PRJ_SMPL_PROC_CD');
    setPrjSmplProcCd(prjSmplProcCd);

    const prdnProcCode = await getCommonCodeNamesCondition({
      optValCtn3: 'Y',
      cmnGrCd: 'PRDN_PROC_CD',
    });
    setPrdnProcCode(prdnProcCode);
  };

  useEffect(() => {
    if (locationState?.elmPrjId) {
      setElmPrjId(locationState.elmPrjId || searchParams.get('elmPrjId') || '');
    }
  }, [locationState?.elmPrjId]);

  useEffect(() => {
    if (elmPrjId) {
      (async () => {
        loadAssignedProjects();

        // [26.08.06]  단계유형 조회 (셋업단계설정이 끝난 단계유형만 조회)
        findValidProjectStepType(elmPrjId).then((result) => {
          setSpTpCds(result);
        });
        await loadProject(elmPrjId).then((result) => {
          loadSampleGrid(elmPrjId);
          loadSetupGrid(elmPrjId);
        });
      })();
    }
  }, [elmPrjId, editMode]);

  useEffect(() => {
    // 대기상태, 수정권한 있으면 편집모드로 전환
    if (pageRegStatCd === 'WAIT' && hasModifyAuth) {
      setEditMode(PageMode.EDIT);
    }
  }, [pageRegStatCd, hasModifyAuth]);

  const [excpIds, setExcpIds] = useState<any>([]);
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]);

  useEffect(() => {
    if (elmPrjId) {
      findExcpByElmPrjId(elmPrjId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }
  }, [elmPrjId]);

  const [assignedProject, setAssignedProject] = useState<any>([]);
  const optionAssignedProject = useMemo(() => {
    return (assignedProject || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.elmPrjId,
          cmnCdNm: cur.elmPrjNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [assignedProject]);

  const loadProject = async (elmPrjId) => {
    return new Promise((resolve, reject) => {
      findProjectById(elmPrjId).then((result: SetupProject) => {
        if (!result) {
          openMessageBar({
            type: 'error',
            content: t('setup.msg.프로젝트 정보가 없습니다.', '프로젝트 정보가 없습니다.'),
          });
          menuContext.closeCurrentTab(menuContext);
          return;
        }

        // 업체
        result.excpIds = result.excpId?.split(',').map((v) => v.trim());
        setExcpIds(
          result.excpId?.split(',').map((id, idx) => ({
            excpId: id.trim(),
            excpIdNm: masterData.excpNm?.split(',')[idx]?.trim(),
          }))
        );

        setEquipmentModalCondition({
          copCd: bildPlntCd.find((item) => item.cmnCd === masterData.bildPlntCd)?.optValCtn4,
        });

        setPageRegStatCd(result?.regStatCd ?? 'WAIT');
        setMasterData(cloneDeep(result));
        resolve(result);
      });

      findSetupEquipmentSummary({ elmPrjId: elmPrjId }).then((result: SetupEquipment[]) => {
        if (result !== null) {
          setSetupSummaryData(result[0]);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      });
    });
  };

  const loadSampleGrid = async (elmPrjId: string) => {
    openLoading(true);
    findSampleSchedule({ elmPrjId: elmPrjId, eqpTpCd: 'PROC' })
      .then((result) => {
        setSampleRowData(result || []);
      })
      .finally(() => openLoading(false));
    return;
  };

  const loadSetupGrid = async (elmPrjId: string) => {
    openLoading(true);
    findSetupEquipment({ elmPrjId: elmPrjId, eqpTpCd: 'EQP' })
      .then((result) => {
        setSetupRowData(result || []);
      })
      .finally(() => openLoading(false));

    return;
  };

  const loadAssignedProjects = () => {
    findAssignedProject().then((result: SetupProject[]) => {
      setAssignedProject(result);
    });
  };

  const handleAssignedProjectChange = (value) => {
    if (value) {
      setEditMode(PageMode.READ);
      setElmPrjId(value);
    }
  };

  const sampleLayoutDefinition = useMemo(() => {
    const prdnProcCodeMap = new DataMap(prjSmplProcCd, 'cmnCd', 'cmnCdDesc');

    return [
      {
        binding: 'eqpId',
        visible: false,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        isReadOnly: true,
        align: 'center',
        visible: isEditable,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        visible: !isEditable,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'prdnProcCd',
        header: String(t('setup.label.공정', '공정')),
        width: 150,
        isReadOnly: !isEditable,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: prdnProcCodeMap,
        cellTemplate: (item) => {
          if (isEditable) {
            if (prdnProcCodeMap.getDisplayValue(item.value) == undefined) {
              return `<span>${item.text}</span>`;
            } else {
              return `<span>${prdnProcCodeMap.getDisplayValue(item.text)}</span>`;
            }
          } else {
            return `<span>${item.item.prdnProcCdNm}</span>`;
          }
        },
      },
      {
        binding: 'smplNm',
        header: String(t('setup.label.샘플/양산 명', '샘플/양산 명')),
        width: '*',
        minWidth: 250,
        isReadOnly: !isEditable,
        align: 'left',
      },
      {
        header: String(t('setup.label.샘플', '샘플')),
        align: 'center',
        columns: [
          {
            binding: 'planStDt',
            header: String(t('setup.label.시작', '시작')),
            width: 130,
            isReadOnly: !isEditable,
            isRequired: false,
            align: isEditable ? 'left' : 'center',
            cssClass: isEditable ? 'WijmoDate' : '',
            format: 'yyyy.MM.dd',
            editor: dateEditor.current,
            cellTemplate: (params) =>
              params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
          },
          {
            binding: 'planEndDt',
            header: String(t('setup.label.종료', '종료')),
            width: 130,
            isReadOnly: !isEditable,
            isRequired: false,
            align: isEditable ? 'left' : 'center',
            cssClass: isEditable ? 'WijmoDate' : '',
            format: 'yyyy.MM.dd',
            editor: dateEditor.current,
            cellTemplate: (params) =>
              params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
          },
        ],
      },
      {
        header: String(t('setup.label.양산', '양산')),
        align: 'center',
        columns: [
          {
            binding: 'prdnStDt',
            header: String(t('setup.label.시작', '시작')),
            width: 130,
            isReadOnly: !isEditable,
            isRequired: false,
            align: isEditable ? 'left' : 'center',
            cssClass: isEditable ? 'WijmoDate' : '',
            format: 'yyyy.MM.dd',
            editor: dateEditor.current,
            cellTemplate: (params) =>
              params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
          },
          {
            binding: 'prdnEndDt',
            header: String(t('setup.label.종료', '종료')),
            width: 130,
            isReadOnly: !isEditable,
            isRequired: false,
            align: isEditable ? 'left' : 'center',
            cssClass: isEditable ? 'WijmoDate' : '',
            format: 'yyyy.MM.dd',
            editor: dateEditor.current,
            cellTemplate: (params) =>
              params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
          },
        ],
      },
    ];
  }, [masterData, isEditable, prdnProcCode]);

  const setupLayoutDefinition = useMemo(() => {
    const prdnProcCodeMap = new DataMap(prdnProcCode, 'cmnCd', 'cmnCdDesc');
    const useYnMap = new DataMap(commonYNList, 'cmnCd', 'cmnCd');
    const projectExcpCodeMap = new DataMap(
      (prjExcpData || []).reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.excpId,
            cmnCdNm: cur.excpNm,
          } as Code,
        ],
        [] as Code[]
      ),
      'cmnCd',
      'cmnCdNm'
    );
    const eltrTpCdCodeMap = new DataMap(eltrTpCd, 'cmnCd', 'cmnCdNm');

    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        visible: !isEditable,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        isReadOnly: true,
        minWidth: 43,
        width: 43,
        align: 'center',
        visible: isEditable,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'prdnProcCd',
        header: String(t('setup.label.공정', '공정')),
        width: 150,
        isReadOnly: !isEditable,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: prdnProcCodeMap,
        cellTemplate: (item) => {
          if (isEditable) {
            if (prdnProcCodeMap.getDisplayValue(item.value) == undefined) {
              return `<span>${item.text}</span>`;
            } else {
              return `<span>${prdnProcCodeMap.getDisplayValue(item.text)}</span>`;
            }
          } else {
            return `<span>${item.item.prdnProcCdNm}</span>`;
          }
        },
      },
      {
        binding: 'spTpCd',
        header: String(t('setup.label.단계유형', '단계유형')),
        minWidth: 80,
        width: 150,
        align: 'center',
        isReadOnly: !isEditable,
        cssClass: isEditable ? 'WijmoSelect' : '',
        dataMap: new DataMap(spTpCds || [], 'spTpCd', 'spTpNm'),
      },
      {
        binding: 'eqclId',
        header: String(t('setup.label.설비군', '설비군')),
        width: 150,
        isReadOnly: true,
        align: 'center',
        cssClass: isEditable ? 'WijmoFind' : '',
        cellTemplate: (params) => ` 
          <span>${params.value || ''}</span>
          <Button id="equipmentGroup" />
        `,
      },
      // ${isEditable ? '<Button id="equipmentGroup" />' : ''}
      {
        binding: 'eltrTpCd',
        header: String(t('setup.label.양/음극', '양/음극')),
        width: 150,
        isReadOnly: !isEditable,
        align: 'center',
        dataMap: eltrTpCdCodeMap,
      },
      {
        binding: 'eqpLnId',
        header: String(t('setup.label.호기', '호기')),
        width: 150,
        isReadOnly: !isEditable,
        align: 'center',
      },
      {
        binding: 'eqpId',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        width: 190,
        isReadOnly: !isEditable,
        align: 'left',
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비명', '설비명')),
        width: '*',
        isReadOnly: !isEditable,
        minWidth: 210,
        align: 'left',
      },
      {
        binding: 'planEndDt',
        header: String(t('setup.label.타겟일정', '타겟일정')),
        width: 130,
        isReadOnly: !isEditable,
        isRequired: false,
        align: isEditable ? 'left' : 'center',
        cssClass: isEditable ? 'WijmoDate' : '',
        format: 'yyyy.MM.dd',
        editor: dateEditor.current,
        cellTemplate: (params) => (params.value ? dayjs(params.value).format('YYYY.MM.DD') : ''),
      },
      {
        binding: 'excpId',
        header: String(t('com.label.협력업체', '협력업체')),
        isReadOnly: !isEditable,
        width: 100,
        align: 'center',
        dataMap: projectExcpCodeMap,
      },
      {
        binding: 'useYn',
        header: String(t('com.label.사용여부', '사용여부')),
        width: 100,
        isReadOnly: !isEditable,
        align: 'center',
        dataMap: useYnMap,
      },
    ];
  }, [masterData, isEditable, prdnProcCode, prjExcpData, eltrTpCd]);

  const onSampleItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (['prdnProcCd'].includes(binding)) {
        cell.ariaReadOnly = !isEditable;
        toggleClass(cell, 'WijmoSelect', !isEditable);
        if (!isEditable && cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }
    }
  });

  const onSetupItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (['prdnProcCd'].includes(binding)) {
        cell.ariaReadOnly = !isEditable;
        toggleClass(cell, 'WijmoSelect', isEditable);
        if (!isEditable && cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }
      if ('eqclId' === binding) {
        cell.ariaReadOnly = !isEditable;
        toggleClass(cell, 'WijmoFind', isEditable);
        if (!isEditable && cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }
      if ('eltrTpCd' === binding) {
        cell.ariaReadOnly = !isEditable;
        toggleClass(cell, 'WijmoSelect', isEditable);
        if (!isEditable && cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }
      if ('excpId' === binding) {
        // 일정이 등록된 설비인 경우 협력업체 변경 불가
        const readonly = !isEditable || item.schdYn === 'Y';
        cell.ariaReadOnly = readonly;
        toggleClass(cell, 'WijmoSelect', readonly);
        if (readonly && cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }

      /*
       20251212 사용여부 수정가능하도록 변경
     */
      // if ('useYn' === binding) {
      //   const readonly = !isEditable || item.crudKey !== CrudCode.CREATE;
      //   cell.ariaReadOnly = readonly;
      //   toggleClass(cell, 'WijmoSelect', readonly);
      //   if (readonly && cell.firstElementChild instanceof HTMLButtonElement) {
      //     cell.firstChild.remove();
      //   }
      // }

      if (CellType.Cell === panel.cellType) {
        const binding = panel.columns[col].binding;
        const item = panel.rows[row].dataItem;

        // 시작일, 종료일 editable css 변경
        if (['planStDt', 'planEndDt'].includes(binding)) {
          cell.ariaReadOnly = !isEditable;
        }
      }
    }
  });

  const onSampleInitialized = (grid) => {
    setSampleFlexRef(grid);
    sampleGridRef.current = grid;
    grid.itemFormatter = onSampleItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.
      const item = grid.rows[ht.row].dataItem; // 선택한 row의 정보
      const col = grid.columns[ht.col];
    });
  };

  const onSetupInitialized = useEvent((grid) => {
    setSetupFlexRef(grid);
    setupGridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLButtonElement) {
        // 설비군
        if ('equipmentGroup' === e.target.id) {
          if (!item.spTpCd) {
            openMessageBar({
              type: 'warning',
              content: String(
                t('setup.label.단계유형을 선택해 주세요.', '단계유형을 선택해 주세요.')
              ),
            });
            return;
          }
          setOpenEquipmentGroupPopup(true);
          setTargetCondition({
            row: ht.row,
            col: ht.col,
            spTpCd: item.spTpCd,
          });
        }
      }
    });
  });

  const beginningSampleEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const newVal = grid.activeEditor?.value;
    const data = grid.rows[e.row].dataItem;
    const binding = grid.columns[e.col].binding;
  });

  const beginningSetupEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;

    const readonly = item.crudKey !== CrudCode.CREATE;
    /*
     20251212 사용여부 수정가능하도록 변경
     */
    // if (['useYn'].includes(binding)) {
    //   e.cancel = readonly;
    // }
    // 일정이 등록된 설비인 경우 협력업체 변경 불가
    if ('excpId' === binding && item.schdYn === 'Y') {
      e.cancel = true;
    }
  });

  const onSetupCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;
    if ('spTpCd' === binding) {
      const newSpTpCd = (spTpCds || []).find((o) => o.spTpNm === newVal);
      // 값이 없는 경우 초기화
      if (!newSpTpCd?.spTpCd) {
        item.eqclId = '';
      }
      if (oldVal !== newSpTpCd?.spTpCd) {
        item.eqclId = '';
      }
    }
  });

  const btnSampleExcelExport = useCallback(() => {
    CommonUtil.exportWijmoExcelGrid(
      sampleFlexRef,
      t('setup.label.샘플일정 목록', '샘플일정 목록'),
      t('setup.label.샘플일정', '샘플일정')
    );
  }, [sampleFlexRef]);

  const btnSetupExcelExport = useCallback(() => {
    CommonUtil.exportWijmoExcelGrid(
      setupFlexRef,
      t('setup.label.셋업설비 목록', '셋업설비 목록'),
      t('setup.label.설비셋업', '설비셋업')
    );
  }, [setupFlexRef]);

  const handleSampleAddRow = useEvent(() => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      useYn: CommonYN.Y,
    } as SetupEquipment;
    const rows = [newRow, ...sampleRowData];
    setSampleRowData(rows);
  });

  const handleSetupAddRow = useEvent(() => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      useYn: CommonYN.Y,
    } as SetupEquipment;
    const rows = [newRow, ...setupRowData];
    setSetupRowData(rows);
  });

  const handleSampleDelRow = useEvent(() => {
    const selectedRowNodes = sampleGridRef.current.selectedRows;
    const isSelected = selectedRowNodes.filter((item) => item.isSelected);
    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    const selectedIds = selectedRowNodes
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (sampleRowData[item].crudKey === CrudCode.CREATE) {
        delete sampleRowData[item];
      } else {
        sampleRowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = sampleRowData.filter((element) => element !== undefined);
    setSampleRowData(filteredData || []);
  });

  const handleSetupDelRow = useEvent(() => {
    const selectedRowNodes = setupGridRef.current.selectedRows;
    const isSelected = selectedRowNodes.filter((item) => item.isSelected);
    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    const selectedData = isSelected.map(({ dataItem }) => dataItem);
    const validRowData = selectedData.filter((item) => item.crudKey !== CrudCode.CREATE);
    if ((validRowData || []).length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'setup.msg.설비가 삭제되면 다시 사용 할 수 없습니다. 삭제 하시겠습니까?',
          '설비가 삭제되면 다시 사용 할 수 없습니다. 삭제 하시겠습니까?'
        ),
        yesCallback: () => {
          deleteSetupDelRow();
        },
        noCallback: () => {
          return false;
        },
      });
    } else {
      deleteSetupDelRow();
    }
  });

  const deleteSetupDelRow = useEvent(() => {
    const selectedRowNodes = setupGridRef.current.selectedRows;
    const selectedIds = selectedRowNodes
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (setupRowData[item].crudKey === CrudCode.CREATE) {
        delete setupRowData[item];
      } else {
        setupRowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = setupRowData.filter((element) => element !== undefined);
    setSetupRowData(filteredData || []);
  });

  const onSetupRefresh = () => {
    loadSetupGrid(elmPrjId);
  };

  const onSampleRefresh = () => {
    loadSampleGrid(elmPrjId);
  };

  const validate = () => {
    if ((sampleRowData || []).length > 0) {
      const validSample = (sampleRowData || [])
        .filter((o) => o.crudKey !== CrudCode.DELETE)
        .map((o, index) => {
          if (_.isEmpty(o.prdnProcCd)) {
            return (
              `샘플일정 (${index + 1}행) : ` +
              t('setup.msg.공정을 입력해 주세요.', `공정을 입력해 주세요.`) +
              '\n'
            );
          }

          if (_.isEmpty(o.smplNm)) {
            return (
              `샘플일정 (${index + 1}행) : ` +
              t('setup.msg.샘플명을 입력해 주세요.', `샘플명을 입력해 주세요.`) +
              '\n'
            );
          }

          o.planStDt = CommonUtil.toYmd(o.planStDt ?? '') ?? '';
          o.planEndDt = CommonUtil.toYmd(o.planEndDt ?? '') ?? '';

          if (o.planStDt && o.planEndDt && o.planStDt > o.planEndDt) {
            return (
              `샘플일정 (${index + 1}행) : ` +
              t(
                'setup.msg.샘플 종료일자는 샘플 시작일자보다 크거나 같아야 합니다.',
                `샘플 종료일자는 샘플 시작일자보다 크거나 같아야 합니다.`
              ) +
              '\n'
            );
          }

          o.prdnStDt = CommonUtil.toYmd(o.prdnStDt ?? '') ?? '';
          o.prdnEndDt = CommonUtil.toYmd(o.prdnEndDt ?? '') ?? '';

          if (o.prdnStDt && o.planEndDt && o.planEndDt > o.prdnStDt) {
            return (
              `샘플일정 (${index + 1}행) : ` +
              t(
                'setup.msg.양산 시작일자는 샘플 종료일자보다 크거나 같아야 합니다.',
                `양산 시작일자는 샘플 종료일자보다 크거나 같아야 합니다.`
              ) +
              '\n'
            );
          }

          if (o.prdnStDt && o.prdnEndDt && o.prdnStDt > o.prdnEndDt) {
            return (
              `샘플일정 (${index + 1}행) : ` +
              t(
                'setup.msg.양산 종료일자는 양산 시작일자보다 크거나 같아야 합니다.',
                `양산 종료일자는 양산 시작일자보다 크거나 같아야 합니다.`
              ) +
              '\n'
            );
          }
        })
        .filter((element) => element !== undefined);

      if (validSample.length) {
        openMessageBar({ type: 'error', content: validSample.toString() });
        return false;
      }
    }

    const validSetup =
      (setupRowData || []).length > 0
        ? (setupRowData || [])
            .filter((o) => o.crudKey !== CrudCode.DELETE)
            .map((o, index) => {
              if (_.isEmpty(o.prdnProcCd)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.공정을 입력해 주세요.', `공정을 입력해 주세요.`) +
                  '\n'
                );
              }

              if (_.isEmpty(o.eqclId)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.설비군을 입력해 주세요.', `설비군을 입력해 주세요.`) +
                  '\n'
                );
              }
              if (_.isEmpty(o.eltrTpCd)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.양/음극을 입력해 주세요.', `양/음극을 입력해 주세요.`) +
                  '\n'
                );
              }

              if (_.isEmpty(o.eqpLnId)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.호기를 입력해 주세요.', `호기를 입력해 주세요.`) +
                  '\n'
                );
              }

              if (_.isEmpty(o.eqpId)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.설비ID를 입력해 주세요.', `설비ID를 입력해 주세요.`) +
                  '\n'
                );
              }

              // [26.08.26] M-CODE 규칙 변경 (최소 4자이상, 공정+설비군 포함)
              const eqpId = o.eqpId || '';
              if (eqpId.length < 4) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.설비ID는 4자리 이상으로 입력해 주세요.', {
                    defaultValue: `설비ID는 4자리 이상으로 입력해 주세요.`,
                  }) +
                  '\n'
                );
              }

              const procEqclId = `${o.prdnProcCd || ''}${o.eqclId || ''}`;
              if (!eqpId.includes(procEqclId)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.설비ID에 공정+설비군({{procEqclId}})이 포함되어야 합니다.', {
                    defaultValue: `설비ID에 공정+설비군({{procEqclId}})이 포함되어야 합니다.`,
                    procEqclId: procEqclId,
                  }) +
                  '\n'
                );
              }

              if (_.isEmpty(o.eqpNm)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.설비명을 입력해 주세요.', `설비명을 입력해 주세요.`) +
                  '\n'
                );
              }

              if (!o.planEndDt) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.타겟일정을 입력해 주세요.', `타겟일정을 입력해 주세요.`) +
                  '\n'
                );
              }

              if (_.isEmpty(o.excpId)) {
                return (
                  `설비셋업 (${index + 1}행) : ` +
                  t('setup.msg.협력업체를 입력해 주세요.', `협력업체를 입력해 주세요.`) +
                  '\n'
                );
              }

              o.planEndDt = CommonUtil.toYmd(o.planEndDt ?? '') ?? '';
              if (!o.planEndDt)
                return (
                  `셋업일정 (${index + 1}행) : ` +
                  t(
                    'setup.msg.종료일 형식이 올바르지 않습니다.',
                    `종료일 형식이 올바르지 않습니다.`
                  ) +
                  '\n'
                );
            })
            .filter((element) => element !== undefined)
        : t('setup.msg.설비셋업 저장할 데이터가 없습니다.', `설비셋업 저장할 데이터가 없습니다.`);

    if (validSetup.length) {
      openMessageBar({ type: 'error', content: validSetup.toString() });
      return false;
    }

    const getDuplicateEqpIds = (rowData) => {
      const countMap = new Map<string, number>();
      const duplicates = new Set<string>();
      for (const row of rowData) {
        const id = row.eqpId ?? '';
        if (id === '') continue;
        const count = (countMap.get(id) ?? 0) + 1;
        countMap.set(id, count);
        if (count > 1) duplicates.add(id);
      }
      return Array.from(duplicates);
    };

    const dupIds = getDuplicateEqpIds(setupRowData);
    if (dupIds.length > 0) {
      const ids = dupIds.join(',');
      openMessageBar({
        type: 'error',
        content: t(`setup.msg.M-CODE({{ids}})가 중복되었습니다.`, {
          defaultValue: `M-CODE({{ids}})가 중복되었습니다.`,
          ids: ids,
        }),
      });
      return false;
    }
    return true;
  };

  const btnSave = useEvent(
    useCallback(
      async (mode: 'DRAFT' | 'SAVE' | 'DELETE') => {
        if (mode !== 'DELETE' && !validate()) {
          return;
        }

        const delSize = (setupRowData || []).filter(
          (item) => item.crudKey == CrudCode.DELETE
        ).length;
        const msg =
          delSize > 0
            ? t(
                `setup.label.설비 삭제 대상 ${delSize}건이 있습니다. 저장하시겠습니까?`,
                `설비 삭제 대상 ${delSize}건이 있습니다. 저장하시겠습니까?`
              )
            : t('com.label.저장하시겠습니까?', '저장하시겠습니까?');
        openCommonModal({
          modalType: 'confirm',
          content:
            mode !== 'DELETE' ? msg : t('com.label.초기화하시겠습니까?', '초기화하시겠습니까?'),
          yesCallback: async () => {
            const response = await saveSetupEquipment({
              elmPrjId: elmPrjId,
              saveType: mode,
              editMode: mode === 'DELETE' ? PageMode.DELETE : editMode,
              sampleList: sampleRowData,
              setupList: setupRowData,
            });

            if (response == null || response?.successOrNot === 'N') {
              const failText =
                response?.data && typeof response?.data === 'string'
                  ? response?.data
                  : t('com.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.');

              openMessageBar({
                type: 'error',
                content: failText,
              });
              return;
            }
            openMessageBar({
              type: 'confirm',
              content:
                mode !== 'DELETE'
                  ? t('com.label.저장되었습니다.', '저장되었습니다.')
                  : t('com.label.초기화되었습니다.', '초기화되었습니다.'),
            });
            if (response.data) {
              // 재조회
              setEditMode(PageMode.READ);
              setElmPrjId(response.data ?? '');
              /*else {
                const searchPageCondition = {
                  upprMnuUrl: location.pathname,
                  mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
                  mnuNm: t('setup.label.프로젝트 등록/조회', '프로젝트 등록/조회'),
                };
                navigate(`/setup/project`, {
                  state: { ...searchPageCondition },
                });
              }*/
            }
          },
        });
      },
      [masterData, sampleRowData, setupRowData]
    )
  );

  const btnCancel = useCallback(async () => {
    // 상세모드로 전환
    setEditMode(PageMode.READ);
  }, [masterData]);

  const btnModify = () => {
    // 수정모드로 전환
    setEditMode(PageMode.EDIT);
  };

  const btnSetupEquipment = () => {
    setOpenEquipmentModal(true);
  };

  const downloadExcelTemplate = async (mode: 'SETUP_DOWN_UPDTEMP' | 'SETUP_DOWN_TEMP') => {
    if (!masterData?.elmPrjId) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.대상 프로젝트가 조회 되지 않았습니다.',
          '프로젝트가 조회 되지 않았습니다.'
        ),
      });
      return;
    }
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.TPL_SETUP_EQP_LIST,
      masterData.elmPrjId ?? '',
      [],
      mode === 'SETUP_DOWN_UPDTEMP'
        ? FileTypeName.TPL_SETUP_EQP_LIST
        : FileTypeName.TPL_SETUP_EQP_EMPTY_LIST
    ).finally(() => openLoading(false));
  };

  const callbackUploadRegist = async (file) => {
    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      'TPL_SETUP_EQP_REGISTER',
      'NORMAL',
      'TB_EELMB_PRJ_TGT_EQP_M',
      masterData.elmPrjId,
      'SU_EQP_REGISTER'
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          loadSetupGrid(elmPrjId);
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const callbackUploadUpdate = async (file) => {
    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      'TPL_SETUP_EQP_UPDATE',
      'NORMAL',
      'TB_EELMB_PRJ_TGT_EQP_M ',
      masterData.elmPrjId,
      'SU_EQP_UPDATE'
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          loadSetupGrid(elmPrjId);
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const onSampleCellEditEnding = (grid, e) => {
    const newVal = grid.activeEditor?.value;
    const data = grid.rows[e.row].dataItem;
    const binding = grid.columns[e.col].binding;
  };

  const renderGridBtn = (
    id:
      | 'SAMPLE_ADD_ROW'
      | 'SETUP_ADD_ROW'
      | 'SAMPLE_DEL_ROW'
      | 'SETUP_DEL_ROW'
      | 'SAMPLE_DOWN_EXCEL'
      | 'SETUP_DOWN_EXCEL'
      | 'SAMPLE_REFRESH'
      | 'SETUP_REFRESH'
      | 'SETUP_UPLOAD_REGIST'
      | 'SETUP_UPLOAD_MODIFY'
      | 'SETUP_DOWN_TEMP'
      | 'SETUP_DOWN_UPDTEMP'
      | 'SETUP_EQP_ADD',
    className: string,
    text: string
  ) =>
    useMemo(() => {
      if (id === 'SAMPLE_ADD_ROW' || id === 'SETUP_ADD_ROW') {
        // 행추가
        if (editMode !== PageMode.EDIT) {
          return;
        }
        if (!hasModifyAuth) return;
      } else if (id === 'SAMPLE_DEL_ROW' || id === 'SETUP_DEL_ROW') {
        // 행삭제
        if (editMode !== PageMode.EDIT) {
          return;
        }
        if (!hasModifyAuth) return;
      } else if (
        id === 'SETUP_DOWN_TEMP' ||
        id === 'SETUP_DOWN_UPDTEMP' ||
        id === 'SETUP_UPLOAD_REGIST' ||
        id === 'SETUP_UPLOAD_MODIFY'
      ) {
        // 템플릿 다운로드, 업로드
        if (editMode !== PageMode.EDIT) {
          return;
        }
        if (!hasModifyAuth) return;
      } else if (id === 'SETUP_EQP_ADD') {
        // 설비ID 추가(EMS)
        if (editMode !== PageMode.EDIT) {
          return;
        }
        if (!hasModifyAuth) return;
      }

      const dispatch: (action: string) => void = (id: string) => {
        switch (id) {
          case 'SAMPLE_ADD_ROW':
            return handleSampleAddRow();
          case 'SETUP_ADD_ROW':
            return handleSetupAddRow();
          case 'SAMPLE_DEL_ROW':
            return handleSampleDelRow();
          case 'SETUP_DEL_ROW':
            return handleSetupDelRow();
          case 'SAMPLE_DOWN_EXCEL':
            return btnSampleExcelExport();
          case 'SETUP_DOWN_EXCEL':
            return btnSetupExcelExport();
          case 'SAMPLE_REFRESH':
            return onSampleRefresh();
          case 'SETUP_REFRESH':
            return onSetupRefresh();
          case 'SETUP_DOWN_UPDTEMP':
            return downloadExcelTemplate(id);
          case 'SETUP_DOWN_TEMP':
            return downloadExcelTemplate(id);
          case 'SETUP_EQP_ADD':
            return btnSetupEquipment();
        }
      };

      if (id === 'SETUP_UPLOAD_REGIST' || id === 'SETUP_UPLOAD_MODIFY') {
        const uploadBtn = (
          <ExcelUploadButton
            onCallback={id === 'SETUP_UPLOAD_REGIST' ? callbackUploadRegist : callbackUploadUpdate}
          />
        );
        return <>{uploadBtn}</>;
      }

      const btn = (
        <Button
          css={IconButton.button}
          className={className}
          disableRipple
          onClick={() => dispatch(id)}
        >
          {text}
        </Button>
      );
      return <>{btn}</>;
    }, [masterData]);

  const renderBtn = (
    id: 'DRAFT' | 'SAVE' | 'DELETE' | 'CANCEL' | 'EDIT',
    className: string,
    text: string
  ) =>
    useMemo(() => {
      if (id === 'SAVE') {
        // 페이지편집상태 : editMode -> edit
        const isVisible = editMode === PageMode.EDIT && isEditing;
        if (!isVisible) {
          return;
        }
        if (!hasModifyAuth) {
          return;
        }
      }
      if (id === 'DRAFT') {
        // 페이지편집상태 : editMode -> edit, 문서상태가 “완료”가 아닐때
        const isVisible = editMode === PageMode.EDIT && isEditing && pageRegStatCd !== 'REG_CPLT';
        if (!isVisible) {
          return;
        }
      } else if (id === 'DELETE') {
        // 페이지편집상태 : editMode -> edit, 문서상태가 “임시저장” 일 때만 보임
        const isVisible = editMode === PageMode.EDIT && isEditing && pageRegStatCd === 'SAVE';
        if (!isVisible) {
          return;
        }
        if (!hasModifyAuth) {
          return;
        }
      } else if (id === 'CANCEL') {
        // 취소버튼 : editMode -> edit
        if (editMode !== PageMode.EDIT) {
          return;
        }
        if (!hasModifyAuth) {
          return;
        }
      } else if (id === 'EDIT') {
        // 수정버튼 : editMode -> Read
        if (editMode !== PageMode.READ) {
          return;
        }
        if (!hasModifyAuth) {
          return;
        }
      }

      const dispatch: (action: string) => void = (id: string) => {
        switch (id) {
          case 'SAVE':
            return btnSave(id);
          case 'CANCEL':
            return btnCancel();
          case 'DELETE':
            return btnSave(id);
          case 'DRAFT':
            return btnSave(id);
          case 'EDIT':
            return btnModify();
        }
      };

      const btn = (
        <Button
          css={IconButton.button}
          className={className}
          disableRipple
          onClick={() => dispatch(id)}
        >
          {text}
        </Button>
      );
      return <>{btn}</>;
    }, [masterData, sampleRowData, setupRowData, hasModifyAuth]);

  return (
    <>
      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span>{t('setup.label.플랜트', '플랜트')}</span>
              </TableCell>
              <TableCell>{masterData.bildPlntCdNm}</TableCell>
              {/*<TableCell>*/}
              {/*  <span>{t('setup.label.구분', '구분')}</span>*/}
              {/*</TableCell>*/}
              {/*<TableCell>{masterData.prjDivsCdNm}</TableCell>*/}
              <TableCell>{t('setup.label.프로젝트명', '프로젝트명')}</TableCell>
              <TableCell colSpan={3}>{masterData.elmPrjNm}</TableCell>
              <TableCell>{t('setup.label.프로젝트 기간', '프로젝트 기간')}</TableCell>
              <TableCell colSpan={2}>
                {CommonUtil.formatPeriod(masterData.ivesStDt ?? '', masterData.ivesEndDt ?? '')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{t('setup.label.FA 담당자(본사)', 'FA 담당자(본사)')}</span>
              </TableCell>
              <TableCell colSpan={2}>{masterData.suHqMgrNm}</TableCell>
              <TableCell>
                <span>{t('setup.label.FA 담당자(법인)', 'FA 담당자(법인)')}</span>
              </TableCell>
              <TableCell colSpan={2}>{masterData.suCopMgrNm}</TableCell>
              <TableCell>
                <span>{t('setup.label.대표 담당자', '대표 담당자')}</span>
              </TableCell>
              <TableCell colSpan={2}>{masterData.reqMgrNm ?? ''}</TableCell>
              {/*<TableCell>*/}
              {/*  <span>{t('setup.label.프로젝트명', '프로젝트명')}</span>*/}
              {/*</TableCell>*/}
              {/*<TableCell colSpan={5}>{masterData.elmPrjNm}</TableCell>*/}
              {/*<TableCell>*/}
              {/*  <span>{t('setup.label.설비셋업상태', '설비셋업상태')}</span>*/}
              {/*</TableCell>*/}
              {/*<TableCell>{masterData?.regStatCdNm}</TableCell>*/}
            </TableRow>
            <TableRow>
              {/*<TableCell>
                <span>{t('setup.label.FA 담당자(본사)', 'FA 담당자(본사)')}</span>
              </TableCell>
              <TableCell colSpan={2}>{masterData.suHqMgrNm}</TableCell>
              <TableCell>
                <span>{t('setup.label.FA 담당자(법인)', 'FA 담당자(법인)')}</span>
              </TableCell>*/}
              {/*<TableCell colSpan={2}>{masterData.suCopMgrNm}</TableCell>*/}
              <TableCell>
                <span>{t('setup.label.협력업체', '협력업체')}</span>
              </TableCell>
              <TableCell colSpan={5}>{masterData.excpNm}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* master grid */}
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.샘플일정', '샘플일정')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(sampleRowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {renderGridBtn('SAMPLE_ADD_ROW', 'plus', t('com.button.행추가', '행추가'))}
          {renderGridBtn('SAMPLE_DEL_ROW', 'minus', t('com.button.행삭제', '행삭제'))}
          <Button
            css={IconButton.button}
            className="Exceldown"
            disableRipple
            onClick={btnSampleExcelExport}
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          {/*{renderGridBtn('SAMPLE_DOWN_EXCEL', 'Exceldown', t('com.button.다운로드', '화면 다운로드'))}*/}
          {renderGridBtn('SAMPLE_REFRESH', 'refresh', t('com.button.새로고침', '새로고침'))}
          {/*<Button css={IconButton.button} className="plus" onClick={handleAddRow} disableRipple>*/}
          {/*  {t('com.button.행추가', '행추가')}*/}
          {/*</Button>*/}
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={sampleLayoutDefinition}
        rowData={sampleRowData}
        allowPinning={false}
        height={108}
        initialized={onSampleInitialized}
        isSelector={true}
        autoCheck={true}
        beginningEdit={beginningSampleEdit}
        cellEditEnding={onSampleCellEditEnding}
        loadedRows={(grid, e) => {
          const items = grid.collectionView
            ? grid.collectionView.sourceCollection
            : grid.itemsSource;
          if (!items || !Array.isArray(items)) {
            return;
          }

          items.forEach((item, rowIndex) => {
            grid.columns.forEach((col, colIndex) => {
              const value = grid.getCellData(rowIndex, colIndex, false);
              if (col.binding !== 'utmNm') return;
              const content = CommonUtil.safeHtmlContents(value, true);
              grid.setCellData(rowIndex, colIndex, content, true);
            });
          });
          grid.invalidate();
        }}
      />

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.설비셋업 목록', '설비셋업 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(setupRowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {/*{renderGridBtn(
            'SETUP_EQP_ADD',
            'plus',
            t('com.button.설비ID 추가(EMS)', '설비ID 추가(EMS)')
          )}*/}
          {renderGridBtn(
            'SETUP_DOWN_TEMP',
            'plus',
            t('com.button.신규/추가 템플릿', '신규/추가 템플릿')
          )}
          {renderGridBtn('SETUP_DOWN_UPDTEMP', 'plus', t('com.button.수정 템플릿', '수정 템플릿'))}
          {renderGridBtn('SETUP_UPLOAD_REGIST', 'plus', t('com.button.업로드', '업로드'))}
          {renderGridBtn('SETUP_ADD_ROW', 'plus', t('com.button.행추가', '행추가'))}
          {renderGridBtn('SETUP_DEL_ROW', 'minus', t('com.button.행삭제', '행삭제'))}
          <Button
            css={IconButton.button}
            className="Exceldown"
            disableRipple
            onClick={btnSetupExcelExport}
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          {/*{renderGridBtn('SETUP_DOWN_EXCEL', 'Exceldown', t('com.button.다운로드', '화면 다운로드'))}*/}
          {renderGridBtn('SETUP_REFRESH', 'refresh', t('com.button.새로고침', '새로고침'))}
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={setupLayoutDefinition}
        rowData={setupRowData}
        initialized={onSetupInitialized}
        beginningEdit={beginningSetupEdit}
        cellEditEnding={onSetupCellEditEnding}
        isSelector={true}
        allowPinning={false}
        height={450}
        itemFormatter={onSetupItemFormatter}
      />

      <GlobalBtnGroup>
        {renderBtn('CANCEL', 'cancel', t('com.button.취소', '취소'))}
        {renderBtn('EDIT', 'edit', t('com.button.수정', '수정'))}
        {renderBtn('DELETE', 'delete', t('com.button.삭제', '삭제'))}
        {renderBtn('DRAFT', 'draft', t('com.button.임시저장', '임시저장'))}
        {renderBtn('SAVE', 'save', t('com.button.저장', '저장'))}
      </GlobalBtnGroup>

      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
      {isDatepickerModalOpen && (
        <DatepickerModal
          open={isDatepickerModalOpen}
          close={() => {
            setDatepickerModalOpen(false);
          }}
          onCallback={(result) => {
            setDate(result);
          }}
          condition={{
            date: date,
          }}
        />
      )}
      {isOpenEquipmentModal && (
        <EquipmentPopUp
          open={isOpenEquipmentModal}
          close={() => setOpenEquipmentModal(false)}
          title={t('setup.label.설비추가', '설비추가')}
          condition={equipmentModalCondition}
          type={'SETUP'} //
          isCopCdFix={true}
          singleSelect={false}
          defaultEquipment={equipmentModalCondition?.rowData}
          onCallback={(result) => {
            const newRow: SetupEquipment[] = [];
            (result.eqpList || []).forEach((row) => {
              newRow.push({
                crudKey: CrudCode.CREATE,
                eqclId: result.equipmentGroup,
                prdnProcCd: result.processCode,
              });
            });

            setSetupRowData([...setupRowData, ...newRow]);
          }}
        />
      )}
      {isOpenEquipmentGroupPopup && (
        <EquipmentGroupPopUp
          open={isOpenEquipmentGroupPopup}
          close={() => setOpenEquipmentGroupPopup(false)}
          type={'setup'}
          condition={{
            equipmentGroup: '',
            elmPrjId: elmPrjId,
            spTpCd: targetCondition?.spTpCd,
          }}
          isOnlyUsed={true}
          onCallback={(value) => {
            if (typeof targetCondition?.row === 'number') {
              const item = setupGridRef.current?.rows[targetCondition?.row].dataItem;
              const equipmentGroup = (value as EquipmentGroupCondition).equipmentGroup;

              if (item['crudKey'] !== CrudCode.CREATE && equipmentGroup !== item['eqclId']) {
                item['crudKey'] = CrudCode.UPDATE;
              }

              setupGridRef.current?.setCellData(
                targetCondition?.row,
                targetCondition?.col,
                equipmentGroup
              );
            }

            setupGridRef.current?.finishEditing();
            setupGridRef.current?.refresh();
          }}
        />
      )}
    </>
  );
};

export default SetupEquipmentRegistPage;
